export type AgentSkillVariant = {
  name: string;
  triggers: string[];
  capabilities?: string[];
  instructions?: string;
  preferredRoutes?: string[];
  rowsStateKey?: string;
  requiredColumns?: string[];
  requiredDimensions?: string[];
  conditions?: Record<string, string>;
  conditionWhen?: string;
  calculationCode?: string;
};

/** 스킬이 정하는 창 고르기. 드라이버의 `WindowPickSpec` 과 같은 모양이다(`driver/src/windowPick.ts`). */
export type EditWindow = {
  /** 목록 줄에서 누르면 창이 뜨는 체크 칸. 이미 켜져 있으면 끝난 줄이다. */
  open: string;
  /** 창 안 표에서 고르는 체크 칸. */
  check: string;
  /** 창의 확정 단추 글자. */
  button: string;
  /** 사람에게 부를 일 이름(`입금처리`). */
  action: string;
  /** 확정 뒤 목록에서 같은 줄을 다시 찾을 칸들. */
  identity: string[];
  /** 창 안에서 고른 줄을 다시 찾을 칸들. */
  windowIdentity: string[];
  /** 이 일로 다루는 줄만 — 칸 → 허용 값. */
  only?: Record<string, string[]>;
  onlyReason?: string;
  /** 말하지 않았을 때 창에서 고르는 규칙: 잔액이 금액과 같은 줄, 없으면 가장 오래된 줄. */
  pick?: { amount: string[]; balance: string; order: string; paid?: string };
  rowLabel?: string[];
  windowLabel?: string[];
};

export type AgentSkill = {
  name: string;
  description: string;
  instructions: string;
  kind: 'metric' | 'artifact';
  triggers: string[];
  /** 사용자 문구가 아니라 TaskSpec과 맞추는 안정적인 업무 ID. */
  capabilities?: string[];
  preferredRoutes?: string[];
  rowsStateKey?: string;
  /**
   * **화면마다 결과 표 이름이 다를 때.** `rowsStateKey` 는 권장 화면 모두에 같은 표가 있다고
   * 본다. 업무일지는 개별업무일지작성(`journalList`)과 개별업무일지[총무팀](`generalJournalList`)
   * 중 한쪽만 메뉴에 있는 사람이 있어 한 스킬이 두 화면을 안다(2026-10-05). 여기 없는 화면은
   * `rowsStateKey` 를 쓴다.
   */
  rowsByRoute?: Record<string, string>;
  requiredColumns?: string[];
  /** 계산 전에 계획이 반드시 보존해야 하는 분류축. */
  requiredDimensions?: string[];
  /**
   * 이 업무의 초안은 **상세행이 있어야 쓸모가 있다.**
   *
   * 견적서에 자재가 없으면 자재청구로 잇지 못하고, 시간외근무보고서에 근무 행이 없으면
   * `경고: 시간외근무 내역이 없습니다` 로 저장이 막힌다. 스킬 문장으로 적어 두어도
   * 계획은 실행마다 행을 넣었다 뺐다 했다 — 그래서 코드에서 거절한다.
   */
  draftRows?: boolean;
  /**
   * 이 업무는 **이미 있는 문서를 고치는 일**이다. 그 표 이름.
   *
   * 고장완료·점검결과가 그렇다 — 접수와 계획은 이미 있다. 계획은 실행마다 흔들려
   * 어떤 때는 신규를 눌렀고(같은 접수가 하나 더 생긴다), 어떤 때는 접수번호를 물었다
   * (사람은 그것을 외우지 않는다). 스킬 문장으로는 안 잡혀 코드에서 거절한다.
   */
  editRows?: string;
  /**
   * 이 화면이 **반드시** 요구하는 초안 칸들. 하나라도 빠지면 저장이 막힌다.
   *
   * 고장완료는 다섯을 다 고르게 한다(처리결과·고장부위·상세·요인·원인). 스킬 문장에
   * 적어 두어도 계획은 매번 하나씩 흘렸다 — `필수입력확인: 고장요인을 선택하세요`.
   */
  requiredFields?: string[];
  /**
   * 고칠 문서를 불러오려면 **반드시 걸어야 하는 조회 조건**의 이름들.
   *
   * 점검결과는 달(`planmon`)을 걸지 않으면 이번 달만 보인다 — 지난달 계획을 고치라는
   * 말에 계획은 조건 없이 조회했고 목록이 비어 `고칠 행을 찾지 못했다` 로 끝났다.
   */
  editConditions?: string[];
  /**
   * 고치는 문서에서 **헤더 칸을 표 칸보다 먼저** 채운다. 입금입력은 입금액이 0이면 미수 체크를
   * 거부한다 — 드라이버의 기본(표 먼저)으로는 체크가 늘 막혔다(2026-09-29 운영 기록 연구).
   */
  fieldsBeforeCells?: boolean;
  /**
   * 칸을 고르는 순간 화면이 **곧바로 서버에 반영하는** 업무다. 준비는 그 행까지만 짚고,
   * 칸은 확정(저장과 같은 Auto 게이트)에서 바꾼다.
   *
   * 점검계획의 점검일자가 그렇다 — 날짜 칸 달력에서 날을 누르면 저장 단추 없이 옮겨진다
   * (2026-09-30 개발 테넌트). 준비에서 채우면 Auto 가 꺼져 있어도 옮겨진다.
   */
  deferCells?: boolean;
  /**
   * 칸을 고칠 표가 문서를 고르는 목록과 다를 때 그 표. 점검계획은 달력(`data`)에서 날을 찾고
   * 그 날 창의 현장 표(`dataAct`)에서 고친다. 모델에게 맡기지 않고 스킬이 정한다.
   */
  editCells?: string;
  /**
   * 그 칸을 사람에게 부를 이름. 창 안 표는 카탈로그에 없어 열 이름을 모른다 — 점검계획의
   * `plandate` 는 달력 목록에서 `계획일자` 지만, 사람이 고르는 창에는 `점검일자` 로 적혀 있다.
   */
  editCellLabels?: Record<string, string>;
  /**
   * **목록 줄에서 창을 열고, 창에서 줄을 골라, 창의 단추로 확정하는 일.** 예금관리의 입금처리가
   * 그렇다(운영 쓰기 확정의 11%, 사용 기록 2026-09-29~10-04) — 은행이 넣은 입금 줄의 처리 체크를
   * 켜면 그 거래처의 미수 창이 뜨고, 미수를 고르고, 창의 `확인` 이 입금처리다.
   *
   * 무엇을 누르는지는 화면의 사정이라 모델에게 맡기지 않고 스킬이 정한다. 창 안 표는
   * `editCells` 다. 준비는 고르기까지, 단추는 저장과 같은 Auto 게이트에서 누른다.
   */
  editWindow?: EditWindow;
  /**
   * 이 스킬은 **문서를 쓴다.** 묻기만 하는 발화에는 고르지 않는다.
   *
   * 트리거는 낱말 겹침이라 묻는 말까지 끌어간다 — `이번 달 출장 몇 번 갔어?` 가
   * 출장보고서 **초안** 스킬에 걸려 근태 화면으로 새고 0회로 답했다. 트리거를 하나씩
   * 손보는 것으로는 같은 일이 계속 난다.
   */
  writes?: boolean;
  /** 화면 표시문이 아니라 실제 state에 들어가야 하는 고정 조건. */
  conditions?: Record<string, string>;
  /** conditions를 적용할 질문 정규식. 없으면 항상 적용한다. */
  conditionWhen?: string;
  calculationCode?: string;
  formats?: ('xlsx' | 'pdf' | 'docx' | 'pptx')[];
  style?: string;
  reportProfile?: {
    title: string;
    audience: string;
    cadence: 'weekly' | 'monthly';
    purpose: string;
    summaryHeading?: string;
    detailHeading?: string;
    accent?: string;
  };
  variants?: AgentSkillVariant[];
  clarification?: { pattern: string; question: string; options: string[] };
  /** 선택기 내부 점수. 프롬프트 계약에는 포함하지 않는다. */
  selectionScore?: number;
};

/**
 * 묻기만 하는 발화인가.
 *
 * 쓰라는 말(`써 줘`·`올려 줘`·`등록해`)이 하나도 없이 묻는 말만 있으면 조회다.
 * `민원 처리 끝냈어, 보고서 써줘` 처럼 둘 다 있으면 쓰는 발화로 본다 — 사람은
 * 대개 한 문장 안에서 사실을 말하고 문서를 청한다.
 */
export function looksLikeQuestion(question: string): boolean {
  const writes = /(써\s*줘|쓰라|작성|등록|올려|신청|만들어|처리해|접수해|입력|정리해|청구해|정산해|잡아\s*줘|내줘|해줘)/;
  if (writes.test(question)) return false;
  return /(몇|얼마|어때|어떻게|어디|있어|있나|있는지|알려|보여|조회|현황|목록|리스트|남았|됐어|됐나|\?)/.test(question);
}

export function selectSkills(question: string, skills: AgentSkill[]): AgentSkill[] {
  const normalized = question.toLocaleLowerCase();
  const asking = looksLikeQuestion(question);
  const requestVerbs = new Set([
    '해줘', '써줘', '올려줘', '내줘', '넣어줘', '적어줘', '만들어줘', '잡아줘', '신청해줘', '등록해줘', '작성해줘',
    '올려', '내려', '잡아', '적어', '넣어', '만들', '작성', '등록', '신청', '초안', '올리기', '보내줘', '주세요', '줘',
  ]);
  const writingWords = /작성|등록|신청|접수|초안|만들|써줘|적어|넣어|올려|잡아/;
  const triggerScore = (trigger: string): number => {
    const words = trigger.toLocaleLowerCase().split(/[^0-9A-Za-z가-힣]+/).filter((word) => word.length >= 2);
    const remaining = words.filter((word) => !requestVerbs.has(word));
    const matched = remaining.length ? remaining : words;
    return matched.length && matched.every((word) => normalized.includes(word))
      ? matched.reduce((total, word) => total + word.length, 0)
      : 0;
  };
  const bonus = writingWords.test(question) ? 100 : 0;
  return skills.flatMap((skill) => {
    // 묻는 발화에는 문서 쓰는 스킬을 고르지 않는다. 그 계약이 조회 계획을 흔든다.
    if (asking && skill.writes) return [];
    const variant = skill.variants?.map((candidate) => ({
      candidate,
      score: Math.max(0, ...candidate.triggers.map(triggerScore)),
    })).filter((entry) => entry.score > 0).sort((left, right) => right.score - left.score)[0];
    if (variant) {
      return [{
        ...skill,
        ...variant.candidate,
        name: `${skill.name}:${variant.candidate.name}`,
        instructions: [skill.instructions, variant.candidate.instructions].filter(Boolean).join('\n'),
        variants: undefined,
        selectionScore: variant.score + (skill.writes ? bonus : 0),
      }];
    }
    const score = Math.max(0, ...skill.triggers.map(triggerScore));
    return score
      ? [{ ...skill, selectionScore: score + (skill.writes ? bonus : 0) }]
      : [];
  }).sort((left, right) => (right.selectionScore ?? 0) - (left.selectionScore ?? 0));
}

/** 최신 사용자의 명시적 분류가 과거 대화에서 선택된 skill을 덮어쓴다. */
/**
 * 되묻기에 **화면 이름으로** 답한 턴을 받아 낸다.
 *
 * 보기는 사무직이 읽는 말이어야 해서 화면 이름(`스케쥴관리`)으로 낸다. 그런데 그 말을
 * 다음 턴에 트리거로 다시 매치하면 엉뚱한 곳으로 간다 — `스케쥴관리` 는 하필
 * `maintenance-visit-schedule` 의 트리거이고 그 스킬은 권장 화면이 비어 있다. 실측에서
 * 보기 아홉 개 중 둘은 틀린 스킬로, 셋은 **아무 스킬도 없는** 곳으로 떨어졌다.
 *
 * 이름을 경로로 되짚어 **그 화면을 권장하는 스킬**을 고르면 그 자리가 사라진다.
 * 변형(`employee-performance-ranking:parts-replacement`)은 제 권장 화면이 없으므로
 * 트리거 매치가 그대로 맡는다 — 그래서 이것은 대체가 아니라 앞에 덧대는 것이다.
 */
export function selectSkillsByScreenName(
  question: string,
  skills: AgentSkill[],
  routeNames: Map<string, string>,
): AgentSkill[] {
  const wanted = question.trim();
  if (!wanted) return [];
  const routes = Array.from(routeNames)
    .filter(([, name]) => name === wanted)
    .map(([route]) => route);
  if (!routes.length) return [];
  return skills.filter((skill) => (skill.preferredRoutes ?? []).some((route) => routes.includes(route)));
}

export function selectSkillsForTurn(
  question: string,
  contextualQuestion: string,
  skills: AgentSkill[],
  routeNames = new Map<string, string>(),
): AgentSkill[] {
  // 되묻기의 보기를 그대로 답한 턴이 먼저다. 트리거로 읽으면 남의 스킬로 간다.
  const picked = selectSkillsByScreenName(question, skills, routeNames);
  if (picked.length) return picked;
  const direct = selectSkills(question, skills);
  return direct.length ? direct : selectSkills(contextualQuestion, skills);
}

/** v2 작업 상태에서는 문구 포함 여부가 아니라 업무 capability와 산출물 계약으로 고른다. */
export function selectSkillsForTask(
  task: { capability: string; artifact?: { format: 'xlsx' | 'docx' | 'pdf' | 'pptx' } },
  skills: AgentSkill[],
  question = '',
): AgentSkill[] {
  const metricSkills = skills.flatMap((skill) => {
    if (skill.kind === 'artifact') return [];
    const variant = skill.variants?.find((candidate) => candidate.capabilities?.includes(task.capability));
    if (variant) {
      return [{
        ...skill,
        ...variant,
        name: `${skill.name}:${variant.name}`,
        instructions: [skill.instructions, variant.instructions].filter(Boolean).join('\n'),
        variants: undefined,
      }];
    }
    return skill.capabilities?.includes(task.capability) ? [skill] : [];
  });
  if (!task.artifact) return metricSkills;

  const candidates = skills.filter((skill) => (
    skill.kind === 'artifact' && skill.formats?.includes(task.artifact!.format)
  ));
  if (!candidates.length) return metricSkills;
  const normalized = question.toLocaleLowerCase();
  const score = (skill: AgentSkill) => Math.max(0, ...skill.triggers.map((trigger) => (
    normalized.includes(trigger.toLocaleLowerCase()) ? trigger.length : 0
  )));
  const matched = candidates
    .map((skill) => ({ skill, score: score(skill) }))
    .filter((entry) => entry.score > 0)
    .sort((left, right) => right.score - left.score)[0]?.skill;
  const fallback = candidates.find((skill) => skill.name === 'executive-report') ?? candidates[0];
  return [...metricSkills, matched ?? fallback!];
}

export function skillClarification(question: string, skills: AgentSkill[], routeNames = new Map<string, string>()): {
  question: string; options: string[];
} | undefined {
  const skill = skills.find((candidate) => (
    !candidate.name.includes(':')
    && candidate.clarification
    && new RegExp(candidate.clarification.pattern).test(question)
  ));
  return skill?.clarification
    ? { question: skill.clarification.question, options: skill.clarification.options }
    : (() => {
      const [first, second] = skills;
      if (!first || !second || Boolean(first.writes) !== Boolean(second.writes)
        || first.selectionScore !== second.selectionScore) return undefined;
      // 자료 스킬과 문서 스킬은 경쟁이 아니라 짝이다 — 하나는 무엇을 셀지, 하나는 어떻게 쓸지다.
      // 「인사회의용 입퇴사 워드 문서」에 둘 중 고르라고 되물어 끝없이 돌았다(2026-10-23).
      if ((first.kind === 'artifact') !== (second.kind === 'artifact')) return undefined;
      // 같은 화면을 여러 스킬이 가리키면 보기가 겹친다. 사람에게 똑같은 버튼 둘을
      // 내밀 수는 없다 — 실제로 `공사현황` 이 두 번 나왔다.
      const options = Array.from(new Set(
        [first, second].flatMap((candidate) => candidate.preferredRoutes ?? [])
          .map((route) => routeNames.get(route) ?? route),
      ));
      // 보기가 하나면 고를 것이 없다. 그 보기를 답해도 같은 두 스킬이 다시 뽑혀 같은 되묻기를 낸다.
      return options.length > 1 ? { question: '어느 업무를 하시려는 건가요?', options } : undefined;
    })();
}

export function describeSkills(skills: AgentSkill[]): string {
  if (!skills.length) return '';
  return `\n적용할 업무 스킬:\n${skills.map((skill) => (
    `[${skill.name}] ${skill.description}\n${skill.instructions}`
      + `${skill.preferredRoutes?.length ? `\n권장 화면: ${skill.preferredRoutes.join(', ')}` : ''}`
      + `${skill.rowsStateKey ? `\n결과 표: ${skill.rowsStateKey}` : ''}`
      // 화면마다 표가 다르면 그것도 적는다 — 안 적으면 모델이 다른 화면에도 같은 표를 낸다.
      + `${skill.rowsByRoute ? Object.entries(skill.rowsByRoute).map(([route, key]) => ` (${route} 에서는 ${key})`).join('') : ''}`
      + `${skill.requiredColumns?.length ? `\n필수 컬럼: ${skill.requiredColumns.join(', ')}` : ''}`
      + `${skill.requiredDimensions?.length ? `\n필수 분류축: ${skill.requiredDimensions.join(', ')}` : ''}`
      + `${skill.capabilities?.length ? `\n업무 capability: ${skill.capabilities.join(', ')}` : ''}`
      + `${skill.conditions ? `\n고정 조건: ${Object.entries(skill.conditions).map(([key, value]) => `${key}=${value}`).join(', ')}` : ''}`
      + `${skill.conditionWhen ? ` (질문 조건: ${skill.conditionWhen})` : ''}`
  )).join('\n\n')}`;
}

/** 그 화면에서 이 스킬의 결과 표. 화면마다 다르면 `rowsByRoute` 가, 아니면 `rowsStateKey` 가 답한다. */
export function skillRowsAt(
  skill: Pick<AgentSkill, 'rowsStateKey' | 'rowsByRoute'>,
  route: string,
): string | undefined {
  return skill.rowsByRoute?.[route] ?? skill.rowsStateKey;
}
